// @flow strict

import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEqual } from 'lodash';

import { reportError } from 'services/Errors/thunks';

import type { ReduxDispatch, ErrorType } from 'types';

// Components
import AppLoader from 'components/AppLoader';
import { MicroFrontendWrapper } from './styles';

type Props = {
    host: string,
    name: string,
    mountedMicroFrontendProps: any,
    isLegacyScriptModule?: boolean,
    /**
     * An MFE is in full screen when it is the only component (except the header) on the page.
     * In pages that contain a sidebar still rendered by this repo (the MS-frontend, i.e.  the host),
     * then the sidebar layout component already removes the header's 61px height, so we don't need
     * to calc the height to remove the header since the sidebar layout removes it.
     *
     * In future iterations of the host application, this will not be necessary since the applications
     * themselves will be responsible for rendering their sidebars.
     *
     * Ex: In MinChem this is not necessary because it renders its own Sidebar.
     * Ex: In SolvExtract plant dashboard and configuration it is necessary
     *  because we use the legacy sidebars.
     */
    mfeIsInFullScreen?: boolean,
    onError: () => void,
    reportError: (error: ErrorType) => void,
};

// TODO: perhaps keeping track of the state would be ideal,
// are we currently loading files? Attempting to mount?
// How many files are loaded etc.

class MicroFrontend extends React.PureComponent<Props> {

    /**
     * When the DIV element is ready we will attempt to mount the MFE.
     */
    componentDidMount() {
        this.loadMFE();
    }

    componentWillUnmount() {
        this.unloadMFE();
    }

    /**
     * Only remount the MFE if the mount props are not deeply equal to each other
     * If they're equal then remounting the microfrontend may cause unintended
     * side-effects like double/triple loading messages, fetches etc.
     */
    componentDidUpdate(prevProps: Props) {
        if(!isEqual(
            prevProps.mountedMicroFrontendProps,
            this.props.mountedMicroFrontendProps)
        ) {
            this.mountMicroFrontend();
        }
    }

    /**
     * Unloads the MFE by calling the unmount function registered by the MFE.
     */
    unloadMFE = () => {
        const unmountFn = window[`unmount${this.props.name}`];
        if (unmountFn) {
            try {
                window[`unmount${this.props.name}`](`${this.props.name}-container`);
            } catch (e) {
                this.onError(e);
            }
        }
    };

    /**
     * When ever an error occurs during loading, let the parent know and report the error.
     */
    onError = (reason) => {
        console.error('Error loading micro-frontend:', reason);
        this.props.onError();
        this.props.reportError({
            type: `front-end-error:error-loading-${this.props.name}-micro-frontend`,
            message: reason instanceof Error ? reason.message : 'Could not load script.',
            stack: reason instanceof Error ? reason.stack : 'No Stack Available.',
        });
    };

    isAlreadyLoaded = (file: string) => {
        const scriptElement = document.getElementById(this.getScriptId(file));
        return Boolean(scriptElement);
    }

    /**
     * Fetch the micro-frontend manifest file, and then add a script with the main file of the MFE.
     */
    loadMFE = () => {
        const isAlreadyLoaded = this.isAlreadyLoaded;
        const mountFn = this.mountMicroFrontend;
        const loadNextStyle = this.loadNextMfeStyle;
        const loadNextScript = this.loadNextMfeScript;
        const errorFn = this.onError;
        fetch(`${this.props.host}/static/asset-manifest.json`)
            .then((res) => {
                if (res.ok) {
                    return res.json();
                } else {
                    throw new Error('Response is not OK.');
                }
            })
            .then((manifest: any) => {
                if(Object.values(manifest.files).map(isAlreadyLoaded).every(Boolean)) {
                    return Promise.resolve();
                }

                return Promise.all(
                    Object.values(manifest.files).map((file) => {
                        if (file.endsWith('.js')) {
                            return loadNextScript(file);
                        }
                        if(file.endsWith('.css')) {
                            return loadNextStyle(file);
                        }
                        return Promise.resolve(); // Add this line to handle non-JS files - like CSS - in the manifest. Otherwise, the promise chain will break. This does nothing, but resolves the promise.
                    })
                );
            })
            .then(mountFn) // After all files are loaded we can mount.
            .catch(errorFn);
    };

    loadNextMfeStyle = (file: string) => {
        const that = this;
        return new Promise(function(resolve, reject) {
            const link = document.createElement('link');
            link.id = that.getScriptId(file);
            link.rel = 'stylesheet';
            link.type = 'text/css';
            link.href = `${that.props.host}${file}`;
            link.crossOrigin = 'anonymous'; // cors must be handled
            link.onload = resolve;
            link.onerror = reject;
            link.onabort = reject;
            (document.head || document.body || document).appendChild(link);
        });
    }

    loadNextMfeScript = (file: string) => {
        const that = this;
        return new Promise(function(resolve, reject) {
            const script = document.createElement('script');
            script.id = that.getScriptId(file);
            script.crossOrigin = 'anonymous'; // cors must be handled
            script.src = `${that.props.host}${file}`;
            if (!that.props.isLegacyScriptModule) {
                script.type = 'module';
            }

            // script.onload = renderFn;
            script.onload = resolve;
            script.onerror = reject;
            script.onabort = reject;
            (document.head || document.body || document).appendChild(script);
        });
    };

    /**
     * A unique ID for each microfrontend script to identify and remove later.
     */
    getScriptId = (file: string) => {
        const strFileName = file.replace(/[^a-zA-Z0-9]/g, '-'); // Replace non-alphanumeric characters with hyphens
        const scriptId = `micro-frontend-script-${this.props.name}-${strFileName}`;
        return scriptId;
    };

    getContainerId = () => `${this.props.name}-container`;

    /**
     * Mount the MFE in react in the container `{mfe-name}-container`
     */
    mountMicroFrontend = () => {
        if(!document.getElementById(this.getContainerId())) {
            // we're not ready to mount yet!
            return;
        }

        const mountFnName = `mount${this.props.name}`;
        const mountFn = window[mountFnName];
        if (mountFn) {
            const params = this.props.mountedMicroFrontendProps;
            try {
                mountFn(this.getContainerId(), params);
            } catch (e) {
                console.error('Received error while mounting MFE:', e);
                this.onError(e);
            }
        }
    };

    render() {
        return (
            <MicroFrontendWrapper
                id={this.getContainerId()}
                excludeHeader={this.props.mfeIsInFullScreen}
            >
                <AppLoader messageId="microfrontends.loadingMFE" loading />
            </MicroFrontendWrapper>
        );
    }
}

const mapDispatchToProps = (dispatch: ReduxDispatch) =>
    bindActionCreators(
        {
            reportError,
        },
        dispatch
    );

export default connect(
    null,
    mapDispatchToProps
)(MicroFrontend);
